import UserInterface from "./UserInterface";
import {Vector as VectorSource} from 'ol/source';
import {Vector as VectorLayer} from 'ol/layer'; // VectorImage as VectorLayer? https://www.gitmemory.com/issue/openlayers/openlayers/12562/887423168
import Shape, {ShapeReactiveProps} from "./Shape.js";
import ShapeAddAction from "../ActionHistory/ShapeAddAction.js";
import {jsPDF} from "jspdf";
import Cutout from "./Cutout";
import {copyObject, isMobile} from "../Util/functions";
import {Feature, MapBrowserEvent} from "ol";
import {primaryAction} from "ol/events/condition";
import predictName from "../Util/NamePredictor";
import {reactive} from "vue";

export type WorkspaceShapeConfig = {
    baseWidth: number,
    fixWidthFromZoom: number|null,
    baseAlpha: number,
    strokeWhite: boolean,
}

export function workspaceShapeConfig(overrides: Partial<WorkspaceShapeConfig> = {}): WorkspaceShapeConfig {
    return copyObject({
        baseWidth: 2,
        fixWidthFromZoom: null,
        baseAlpha: 0.4,
        strokeWhite: false,
    }, overrides);
}

export type ShapeCollectionReactiveProps = {
    focusedShapeRP: ShapeReactiveProps|null,
};

export default class ShapeCollection {

    private reactiveShapesProps: ShapeReactiveProps[];
    reactiveProps : ShapeCollectionReactiveProps;

    readonly mainLayer: VectorLayer<any>;
    readonly mainSource: VectorSource<any>;

    readonly modifyLayer: VectorLayer<any>;
    readonly modifySource: VectorSource<any>;

    readonly drawLayer: VectorLayer<any>;
    readonly drawSource: VectorSource<any>;

    private shapes: Shape[] = [];
    private focusedShape: Shape|null = null;

    private workspaceShapeConfig: WorkspaceShapeConfig = workspaceShapeConfig();

    constructor(readonly userInterface: UserInterface) {
        this.reactiveShapesProps = reactive([]);
        this.reactiveProps = reactive({
            focusedShapeRP: null,
        });

        const olMap = userInterface.getMap().getOpenlayersMap();

        this.mainSource = new VectorSource();
        this.modifySource = new VectorSource();
        this.drawSource = new VectorSource();

        this.mainLayer = new VectorLayer({
            source: this.mainSource,
        });
        this.modifyLayer = new VectorLayer({
            source: this.modifySource,
        });
        this.drawLayer = new VectorLayer({
            source: this.drawSource,
        });

        olMap.addLayer(this.mainLayer);
        olMap.addLayer(this.modifyLayer);
        olMap.addLayer(this.drawLayer);

        document.addEventListener('keydown', (evt) => {
            if (this.focusedShape) {
                this.focusedShape.handleKeyDown(evt);
            }
        });

        if (!isMobile()) {
            olMap.on('singleclick', (e: MapBrowserEvent<any>) => {
                if (e.defaultPrevented) {
                    return;
                }

                if (!primaryAction(e)) {
                    return;
                }

                if (this.userInterface.isLocked()) {
                    return;
                }

                const shapeFeature = olMap.forEachFeatureAtPixel(e.pixel, function (feature, layer) {
                    if (feature && feature.get('shape')) {
                        return feature;
                    }
                }, {
                    layerFilter: (layer) => {
                        return layer == this.mainLayer;
                    }
                });

                if (shapeFeature) {
                    const shape: Shape = shapeFeature.get('shape');

                    e.preventDefault();
                    e.stopPropagation();

                    this.focusShape(shape);
                }
            });
        }
    }

    public getOpenlayersMap()
    {
        return this.userInterface.getMap().getOpenlayersMap();
    }

    public getShapes()
    {
        return this.shapes;
    }

    public find(id: number)
    {
        return this.shapes.find(shape => shape.id === id);
    }

    public getReactiveShapesProps(): ShapeReactiveProps[]
    {
        return this.reactiveShapesProps;
    }

    public updateReactiveShapesProps(): void
    {
        this.reactiveShapesProps.splice(0, this.reactiveShapesProps.length, ...this.shapes.map(shape => shape.reactiveProps));
    }

    public getWorkspaceShapeConfig(): WorkspaceShapeConfig
    {
        return this.workspaceShapeConfig;
    }

    public setWorkspaceShapeConfig(config: WorkspaceShapeConfig): void
    {
        this.workspaceShapeConfig = config;

        for (const shape of this.shapes) {
            shape.updateWorkspaceShapeConfig();
        }
    }

    public workspaceZoomed(): void
    {
        if (this.workspaceShapeConfig.fixWidthFromZoom !== null) {
            for (const shape of this.shapes) {
                shape.updateWorkspaceShapeConfig();
            }
        }
    }

    public hasFocusedShape()
    {
        return this.focusedShape !== null;
    }

    public getFocusedShape(): Shape|null
    {
        return this.focusedShape;
    }

    public unfocus()
    {
        if (this.focusedShape) {
            this.focusedShape.setFocus(false);
            this.reactiveProps.focusedShapeRP = this.focusedShape = null;
        }
    }

    public focusShape(shape: Shape)
    {
        this.unfocus();
        this.userInterface.getRouteCollection().unfocus();

        shape.setFocus(true);
        this.focusedShape = shape;
        this.reactiveProps.focusedShapeRP = this.focusedShape.reactiveProps;
    }

    public addShape()
    {
        const shape = new Shape(this);
        shape.setColor(this.userInterface.newColor());
        shape.setName(predictName(this.shapes.map((shape) => shape.getName())) || 'Vorm ' + (shape.id + 1));

        this.userInterface.actionHistory.addAction(new ShapeAddAction(
            shape,
            this.shapes.length
        ));

        return shape;
    }

    public attachShape(shape: Shape, position: number)
    {
        shape.mouseout(false);
        shape.addToMap();
        this.shapes.splice(position, 0, shape);
        this.updateReactiveShapesProps();

        this.userInterface.showObjectListPanel('shape');
    }

    public detachShape(shape: Shape)
    {
        const index = this.shapes.indexOf(shape);
        if(index === -1) {
            throw new Error('Invalid shape');
        }

        if (shape.hasFocus()) {
            this.unfocus();
        }

        shape.removeFromMap();
        this.shapes.splice(index, 1);
        this.updateReactiveShapesProps();
    }

    public moveShapeInList(shape: Shape, newIndex: number): void {
        const index = this.shapes.indexOf(shape);
        if (index === -1) {
            throw new Error('Invalid shape');
        }

        this.shapes.splice(index, 1);
        this.shapes.splice(newIndex, 0, shape);

        this.updateReactiveShapesProps();
        this.userInterface.showObjectListPanel('shape');
    }

    public checkCloseShapesPanel(): void
    {
        // If no shapes anymore, close shapes panel
        if (this.shapes.length === 0 && this.userInterface.getOpenObjectTypeTab() === 'shape') {
            this.userInterface.showObjectListPanel('cutout');
        }
    }

    public addShapeFromSketch()
    {
        const coordinates = this.sketchShape.getCoordinates();
        const shape = this.addShape();
        shape.setCoordinates(coordinates);
    }

    public duplicateShape(sourceShape: Shape)
    {
        const shape = sourceShape.clone();

        shape.setColor(this.userInterface.newColor());
        shape.setName(sourceShape.getName() + ' (kopie)');

        this.userInterface.actionHistory.addAction(new ShapeAddAction(
            shape,
            this.shapes.length
        ));
    }

    public drawOnPdf(doc: jsPDF, cutout: Cutout<any, any, any>)
    {
        for (const shape of this.shapes) {
            if (shape.isVisible()) {
                shape.drawOnPdf(doc, cutout);
            }
        }
    }
}
